import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Bucket, CorsRule } from '@storeflare/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { api, WebApiError } from '@/lib/api';

const HTTP_METHODS = ['GET', 'PUT', 'POST', 'DELETE', 'HEAD'] as const;

export function BucketSettings({ bucket }: { bucket: Bucket }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [rules, setRules] = useState<CorsRule[]>(bucket.cors_config ?? []);
  const [dirty, setDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () =>
      api<{ id: string; status: string }>(`/v1/buckets/${bucket.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['buckets'] });
      toast.success(`Bucket "${bucket.name}" deleted`);
      navigate('/buckets');
    },
    onError: (err) => {
      // The API returns 409 with code CONFLICT when the bucket still has
      // active files — surface that message instead of the generic one.
      const message =
        err instanceof WebApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to delete bucket';
      setDeleteError(message);
      toast.error(message);
    },
  });

  // Reset local rules when switching to a different bucket.
  useEffect(() => {
    setRules(bucket.cors_config ?? []);
    setDirty(false);
  }, [bucket.id, bucket.cors_config]);

  const patch = useMutation({
    mutationFn: (body: { public_access?: boolean; cors_config?: CorsRule[] }) =>
      api<Bucket>(`/v1/buckets/${bucket.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['bucket', bucket.id] });
      qc.invalidateQueries({ queryKey: ['buckets'] });
      setDirty(false);
      if (typeof vars.public_access === 'boolean') {
        toast.success(vars.public_access ? 'Bucket is now public' : 'Bucket is now private');
      } else if (vars.cors_config) {
        toast.success('CORS rules saved');
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save bucket');
    },
  });

  function updateRule(idx: number, patchRule: Partial<CorsRule>) {
    setRules((prev) =>
      prev.map((r, i) => (i === idx ? sanitizeRule({ ...r, ...patchRule }) : r)),
    );
    setDirty(true);
  }

  function addRule() {
    setRules((prev) => [
      ...prev,
      { allowed_origins: [], allowed_methods: ['GET', 'PUT'], allowed_headers: ['*'] },
    ]);
    setDirty(true);
  }

  function removeRule(idx: number) {
    setRules((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  }

  async function saveCors() {
    await patch.mutateAsync({ cors_config: rules.map(sanitizeRule) });
  }

  return (
    <div className="space-y-6">
      <SectionPublicAccess
        value={bucket.public_access}
        bucketId={bucket.id}
        onToggle={(next) => patch.mutate({ public_access: next })}
        pending={patch.isPending}
      />

      <Separator />

      <section>
        <div className="mb-3 flex items-end justify-between">
          <div>
            <h3 className="text-sm font-semibold">CORS rules</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Controls which browser origins can upload to this bucket via{' '}
              <code className="rounded bg-muted px-1 py-0.5">POST /v1/buckets/&lt;id&gt;/upload</code>.
              Stored on the bucket; enforcement for third-party origins is in progress.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={addRule}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No CORS rules. Add one to allow a browser origin to upload directly.
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, idx) => (
              <CorsRuleEditor
                key={idx}
                rule={rule}
                onChange={(p) => updateRule(idx, p)}
                onRemove={() => removeRule(idx)}
              />
            ))}
          </div>
        )}

        {patch.isError ? (
          <p className="mt-2 text-sm text-destructive">
            {patch.error instanceof Error ? patch.error.message : 'Failed to save'}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || patch.isPending}
            onClick={() => {
              setRules(bucket.cors_config ?? []);
              setDirty(false);
            }}
          >
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || patch.isPending} onClick={saveCors}>
            {patch.isPending ? 'Saving…' : 'Save CORS'}
          </Button>
        </div>
      </section>

      <Separator />

      <section>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-destructive">Delete bucket</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Permanently deletes this bucket. The bucket must be empty (no active files) —
                delete the files first.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
            >
              Delete bucket
            </Button>
          </div>
        </div>
      </section>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bucket?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{bucket.name}</span> will be
              permanently deleted. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError ? (
            <p className="text-sm text-destructive">{deleteError}</p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                remove.mutate();
              }}
              disabled={remove.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? 'Deleting…' : 'Delete bucket'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionPublicAccess({
  value,
  bucketId,
  onToggle,
  pending,
}: {
  value: boolean;
  bucketId: string;
  onToggle: (next: boolean) => void;
  pending: boolean;
}) {
  const apiBase = (import.meta.env['VITE_API_BASE'] ?? '/api').replace(/\/$/, '');
  // The exposed public URL pattern matches the /p/:bucket_id/:file_id/:filename
  // route on the API Worker.
  const examplePath = `${apiBase}/p/${bucketId}/<file_id>/<filename>`;

  return (
    <section>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold">Public access</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            When enabled, files in this bucket are served by their canonical public URL without
            authentication.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={value ? 'destructive' : 'secondary'}>
            {value ? 'public' : 'private'}
          </Badge>
          <Button
            size="sm"
            variant={value ? 'outline' : 'default'}
            disabled={pending}
            onClick={() => onToggle(!value)}
          >
            {pending ? '…' : value ? 'Make private' : 'Make public'}
          </Button>
        </div>
      </div>
      {value ? (
        <div className="mt-3 rounded-md border bg-muted/30 p-3">
          <div className="text-xs text-muted-foreground">Public URL pattern</div>
          <code className="mt-1 block break-all text-xs">{examplePath}</code>
        </div>
      ) : null}
    </section>
  );
}

function CorsRuleEditor({
  rule,
  onChange,
  onRemove,
}: {
  rule: CorsRule;
  onChange: (patch: Partial<CorsRule>) => void;
  onRemove: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-3">
            <div>
              <Label className="text-xs">Allowed origins (one per line)</Label>
              <textarea
                value={(rule.allowed_origins ?? []).join('\n')}
                onChange={(e) =>
                  onChange({ allowed_origins: splitLines(e.target.value) })
                }
                placeholder="https://app.example.com"
                rows={2}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div>
              <Label className="text-xs">Allowed methods</Label>
              <div className="mt-1.5 flex flex-wrap gap-3">
                {HTTP_METHODS.map((m) => {
                  const checked = (rule.allowed_methods ?? []).includes(m);
                  return (
                    <label key={m} className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const set = new Set(rule.allowed_methods ?? []);
                          if (e.target.checked) set.add(m);
                          else set.delete(m);
                          onChange({ allowed_methods: Array.from(set) });
                        }}
                      />
                      <span>{m}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Allowed headers (comma-sep)</Label>
                <Input
                  className="mt-1"
                  value={(rule.allowed_headers ?? []).join(', ')}
                  onChange={(e) =>
                    onChange({ allowed_headers: splitCsv(e.target.value) })
                  }
                  placeholder="*"
                />
              </div>
              <div>
                <Label className="text-xs">Expose headers (comma-sep)</Label>
                <Input
                  className="mt-1"
                  value={(rule.expose_headers ?? []).join(', ')}
                  onChange={(e) =>
                    onChange({ expose_headers: splitCsv(e.target.value) })
                  }
                  placeholder="etag"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Max age (seconds)</Label>
              <Input
                type="number"
                min={0}
                className="mt-1 w-32"
                value={rule.max_age_seconds ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    max_age_seconds: v === '' ? undefined : Math.max(0, Number(v)),
                  });
                }}
                placeholder="600"
              />
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onRemove} title="Remove rule">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function splitLines(s: string): string[] {
  return s
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function splitCsv(s: string): string[] {
  return s
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function sanitizeRule(rule: CorsRule): CorsRule {
  const result: CorsRule = {
    allowed_origins: rule.allowed_origins ?? [],
    allowed_methods: rule.allowed_methods ?? [],
  };
  if (rule.allowed_headers?.length) result.allowed_headers = rule.allowed_headers;
  if (rule.expose_headers?.length) result.expose_headers = rule.expose_headers;
  if (typeof rule.max_age_seconds === 'number') result.max_age_seconds = rule.max_age_seconds;
  return result;
}
